// ---------------------------------------------------------------------------
// The spike of carts waiting to come back.
//
// A drawer rather than a page, because a cashier opens it mid-service with a
// customer in front of them: it slides over the till, they find the cart, and
// the till is back. Navigating away and back would lose whatever is on screen.
//
// The "Everyone" tab appears only for somebody holding `held_orders.view_all`.
// Not hidden-but-present: the request itself is refused without it, so showing
// the tab would produce a 403 the cashier cannot act on.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { PauseCircle, RotateCcw, Trash2, X } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '../../i18n/I18nProvider';
import { useOverlayDismiss } from '../common/overlayStack';
import { usePermission } from '../../hooks/usePermissions';
import { useCancelHeldOrder, useHeldOrders } from '../../api/heldOrders.api';
import { EmptyState, ErrorState, SkeletonRows } from '../common/StateBlocks';
import { formatRelative, formatUsd } from '../../utils/format';
import { ApiError } from '../../lib/apiClient';
import { PrintHeldOrderButton } from './PrintHeldOrderButton';
import type { HeldOrder } from '../../types';

export function HeldOrdersDrawer({
  open,
  onClose,
  onRestore,
  restoringId,
  cartHasItems,
}: {
  open: boolean;
  onClose: () => void;
  onRestore: (held: HeldOrder) => void;
  restoringId: string | null;
  /// Restoring on top of a cart already in progress would silently merge two
  /// customers' orders, so the button is disabled and the reason is shown.
  cartHasItems: boolean;
}) {
  const { t, dir } = useI18n();
  const canSeeAll = usePermission('held_orders.view_all');

  const [scope, setScope] = useState<'own' | 'all'>('own');
  const [confirming, setConfirming] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useHeldOrders({ scope }, open);
  const cancelHeld = useCancelHeldOrder();

  // Escape closes the drawer - the topmost panel only.
  useOverlayDismiss(open, onClose);

  if (!open) return null;

  const cancel = async (id: string) => {
    setProblem(null);

    try {
      await cancelHeld.mutateAsync({ id, reason: reason.trim() });
      setConfirming(null);
      setReason('');
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : t.errors.unexpectedBody);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-label={t.held.heldOrders}>
      <button
        type="button"
        className="flex-1 bg-black/40"
        onClick={onClose}
        aria-label={t.common.close}
      />

      <aside
        className={clsx(
          'flex h-full w-full max-w-md flex-col bg-surface shadow-xl',
          dir === 'rtl' ? 'border-e border-line' : 'border-s border-line',
        )}
      >
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 className="flex items-center gap-2 text-base font-semibold text-ink">
            <PauseCircle className="h-5 w-5 text-butcher-green" aria-hidden="true" />
            {t.held.heldCount.replace('{count}', String(data?.length ?? 0))}
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="rounded-control p-1.5 text-ink-muted hover:bg-surface-soft hover:text-ink"
            aria-label={t.common.close}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        {canSeeAll ? (
          <div className="flex gap-1 border-b border-line px-4 py-2" role="tablist">
            {(['own', 'all'] as const).map((option) => (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={scope === option}
                onClick={() => setScope(option)}
                className={clsx(
                  'min-h-9 rounded-control px-3 text-sm font-medium transition-colors',
                  scope === option
                    ? 'bg-butcher-green/10 text-butcher-green'
                    : 'text-ink-muted hover:text-ink',
                )}
              >
                {option === 'own' ? t.held.mine : t.held.everyone}
              </button>
            ))}
          </div>
        ) : null}

        {cartHasItems ? (
          <p className="border-b border-line bg-antique-gold/10 px-4 py-2.5 text-xs text-antique-gold">
            {t.held.cartNotEmpty}
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-4">
              <SkeletonRows rows={3} />
            </div>
          ) : error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : (data ?? []).length === 0 ? (
            <EmptyState title={t.held.nothingHeld} description={t.held.nothingHeldHint} />
          ) : (
            <ul className="divide-y divide-line">
              {(data ?? []).map((held) => (
                <li key={held.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink">
                        {held.label ?? held.customer?.name ?? held.customerName ?? held.holdNumber}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-muted">
                        {held.holdNumber} · {t.held.items.replace('{count}', String(held.itemCount))}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-muted">
                        {t.held.heldBy.replace('{name}', held.cashier.fullName)} ·{' '}
                        {formatRelative(held.createdAt, 'en')}
                      </p>
                      {held.phone ? (
                        <p className="mt-0.5 text-xs text-ink-muted" dir="ltr">
                          {held.phone}
                        </p>
                      ) : null}
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <span className="numeric text-sm font-semibold text-ink">
                        {formatUsd(held.estimatedTotalUsd)}
                      </span>

                      <div className="flex flex-wrap justify-end gap-1.5">
                        <button
                          type="button"
                          className="btn-primary min-h-9 gap-1.5 text-xs"
                          disabled={cartHasItems || restoringId === held.id}
                          onClick={() => onRestore(held)}
                        >
                          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                          {restoringId === held.id ? t.held.restoring : t.held.restore}
                        </button>

                        {/* Paper for the customer to look at while they wait.
                            It changes nothing about the cart - it is still
                            here, still unpaid, still holding no stock. */}
                        <PrintHeldOrderButton held={held} />

                        <button
                          type="button"
                          className="rounded-control p-2 text-ink-muted transition-colors hover:bg-danger/10 hover:text-danger"
                          title={t.held.cancel}
                          onClick={() => {
                            setConfirming(held.id);
                            setReason('');
                            setProblem(null);
                          }}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">{t.held.cancel}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {confirming === held.id ? (
                    <div className="mt-3 rounded-control border border-danger/40 bg-danger/5 p-3">
                      <p className="text-sm font-medium text-ink">{t.held.cancelTitle}</p>
                      <p className="mt-0.5 text-xs text-ink-muted">{t.held.cancelBody}</p>

                      <label htmlFor={`reason-${held.id}`} className="label mt-3 text-xs">
                        {t.held.cancelReason}
                      </label>
                      <input
                        id={`reason-${held.id}`}
                        type="text"
                        className="input"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                      />

                      {problem ? (
                        <p className="mt-2 text-xs text-danger" role="alert">
                          {problem}
                        </p>
                      ) : null}

                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          className="btn-danger text-xs"
                          disabled={reason.trim().length < 3 || cancelHeld.isPending}
                          onClick={() => void cancel(held.id)}
                        >
                          {t.held.cancelConfirm}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          onClick={() => setConfirming(null)}
                        >
                          {t.common.cancel}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}
